/**
 * B632 (e) — the guest's CHANGE sheet (650:165): the party, the night being
 * left, the schedule's showtimes by day (the current one and a sold-out one
 * visible and unpickable), and — once one is picked — what happens. One
 * door names where they are going; a refusal stands in the sheet in the
 * server's words. Face only; `meApp.tsx` holds the calls.
 */
import type { ReactElement } from "react";
import { Banner, Button, RadioGroup, RightSheet } from "@transact/ui";
import { meMoveOptions, meMoveSummary, meMoveWhere, type MeMoveDay, type MeMoveTarget } from "../lib/meMove.js";

export function MeChangeSheet(props: {
  readonly open: boolean;
  /** The tenant's slot noun (B126): a SHOW's showtime, a FERRY's departure. */
  readonly unit: string;
  readonly target: MeMoveTarget;
  /** "Friday, October 9 · 7:00 PM" — the night being left, in the order page's words. */
  readonly nowWords: string | undefined;
  /** undefined = still reading the showtimes. */
  readonly days: readonly MeMoveDay[] | undefined;
  readonly picked: string | undefined;
  readonly currency: string;
  readonly busy?: boolean;
  readonly error?: string | undefined;
  readonly onPick: (slotId: string) => void;
  readonly onMove: () => void;
  readonly onClose: () => void;
}): ReactElement | null {
  const { target, unit } = props;
  const tickets = `${String(target.count)} ${target.count === 1 ? "ticket" : "tickets"}`;
  const now = props.nowWords !== undefined ? `Now: ${props.nowWords}. ` : "";
  const anyOpen = props.days?.some((d) => d.rows.some((r) => r.open)) === true;
  const where = props.picked !== undefined ? meMoveWhere(props.picked) : undefined;
  return (
    <RightSheet
      id="me-change"
      open={props.open}
      kicker={`Change the ${unit}`}
      title={`Move ${tickets} to…`}
      lede={`${now}Same price, same tickets — you can move until the ${unit} starts.`}
      onClose={props.onClose}
      redline="me-change"
      actions={
        <>
          <Button variant="neutral" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={props.busy === true}
            disabled={where === undefined}
            {...(where === undefined ? { describedBy: "me-change-why" } : {})}
            onClick={props.onMove}
          >
            {where === undefined ? "Move" : `Move to ${where}`}
          </Button>
        </>
      }
      status={where === undefined && anyOpen ? <span id="me-change-why">{`Pick a new ${unit}.`}</span> : undefined}
    >
      {props.days === undefined ? (
        <p className="me-change-quiet" role="status">{`Finding the other ${unit}s…`}</p>
      ) : !anyOpen ? (
        <p className="me-change-quiet">{`There’s no other ${unit} to move to right now. Reply to your confirmation email and the box office will help.`}</p>
      ) : (
        props.days.map((d) => (
          <RadioGroup
            key={d.day}
            legend={d.day}
            name="me-change"
            value={props.picked ?? ""}
            onChange={props.onPick}
            options={meMoveOptions(d)}
          />
        ))
      )}
      {props.picked !== undefined ? (
        <dl className="me-change-summary">
          {meMoveSummary(target, props.picked, props.currency).map((r) => (
            <div className="me-paid-row" key={r.label}>
              <dt>{r.label}</dt>
              <dd>{r.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {props.error !== undefined ? <Banner tone="warn">{props.error}</Banner> : null}
    </RightSheet>
  );
}
